import { useState } from 'react';
import { UserPlusIcon, UserMinusIcon, Loader2Icon } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { followUser } from '@/lib/api';
import Button from '@/components/form/Button';
import { useDebounce } from 'use-debounce';

interface FollowButtonProps {
  userId: string;
  isFollowing: boolean;
  onFollowChange?: () => void;
  size?: 'sm' | 'md' | 'lg';
}

export default function FollowButton({ userId, isFollowing, onFollowChange, size = 'md' }: FollowButtonProps) {
  const [loading, setLoading] = useState(false);
  const [following, setFollowing] = useState(isFollowing);
  const [debouncedFollowChange] = useDebounce(onFollowChange, 500);

  const handleClick = async () => {
    if (loading) return; // Prevent multiple clicks while loading

    try {
      setLoading(true);
      await followUser(userId);
      setFollowing(!following);
      toast.success(following ? 'Unfollowed successfully' : 'Following successfully');
      debouncedFollowChange?.();
    } catch (error) {
      toast.error('Failed to update follow status');
      // Reset state on error
      setFollowing(isFollowing);
    } finally {
      // Add a small delay before enabling the button again
      setTimeout(() => {
        setLoading(false);
      }, 300);
    }
  };

  return (
    <Button
      variant={following ? 'outline' : 'primary'}
      size={size}
      onClick={handleClick}
      disabled={loading}
      icon={loading ? (
        <Loader2Icon className="h-4 w-4 animate-spin" />
      ) : following ? (
        <UserMinusIcon className="h-4 w-4" />
      ) : (
        <UserPlusIcon className="h-4 w-4" />
      )}
    >
      {following ? 'Unfollow' : 'Follow'}
    </Button>
  );
}